import type { NavigationGuard } from 'vue-router';
import { getUserProfile } from '@/api/auth';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { getCorrelationId, getUrlBase, setAccessToken, setRefreshToken, TENANT } from '@/utils/helpers';

/**
 * This guard redirects the user to the SSO login page if the company is using SSO.
 */
export const redirectToSsoLoginIfUsingSso: NavigationGuard = async (to) => {
  const companyStore = useCompanyStore();

  if (companyStore.isUsingSSO) {
    const authStore = useAuthStore();
    await authStore.setUserProfileAndLoggedInState();

    if (authStore.loggedIn) {
      return { name: 'home' };
    }

    const samlUrl = getUrlBase() + '/api/saml2/login';

    window.location.href = samlUrl;

    return false;
  }
};

/**
 * This guard redirects the user to the new tenant specific login page when the user tries to use the old link.
 * Eg. http://instacart.localhost:8080/login will be redirected to http://instacart.localhost:8080/welcome-back
 *
 */
export const handlePreviousTenantSpecificLoginRoute: NavigationGuard = async (to, from, next) => {
  if (TENANT) {
    return next({ name: 'welcome-back' });
  }

  return next();
};

/**
 * This guard handles the SAML login flow.
 * It sets the access token and refresh token in the local storage and redirects the user to the home page.
 */
export const handleSamlLoginRedirect: NavigationGuard = async (to, from, next) => {
  const accessToken = to.query.access_token;
  const refreshToken = to.query.refresh_token;

  if (!accessToken || !refreshToken || typeof accessToken !== 'string' || typeof refreshToken !== 'string') {
    return next({ name: 'welcome-back' });
  }

  setAccessToken(accessToken);
  setRefreshToken(refreshToken);

  const { error } = await getUserProfile();

  if (error) {
    return next({ name: 'welcome-back' });
  } else {
    return next({ name: 'home' });
  }
};

export const goHomeIfNoCorrelationId: NavigationGuard = async (to, from, next) => {
  if (getCorrelationId() === null) {
    return next({ name: 'home' });
  }
  return next();
};
